"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import { Eye, Brain, GitBranch, Workflow, Layers, Compass, ArrowRight, Search, Cog, Target, Shield, Activity, Network, Radar, Send } from "lucide-react";
import { Section } from "@/components/layout/section";
import { SectionHeader } from "@/components/ui/section-header";

const STAGES = [
  { num: "01", icon: Eye, title: "Intelligence", body: "Every engagement begins with intelligence gathering, not assumptions. Real-time monitoring across 200+ digital surfaces and 100K+ news sources. Ground-truth intelligence gathered before any strategy is formed. The competitive environment mapped.", tools: ["Real-time monitoring", "Multi-source intelligence", "Competitive mapping"], accent: "red" },
  { num: "02", icon: Brain, title: "Analysis", body: "Pattern identification, anomaly detection, correlation mapping, and causal analysis. Monitoring data flows through predictive models and outcome-forecasting engines. The analysis is forensic — not descriptive, but diagnostic.", tools: ["Predictive models", "Outcome forecasting", "Root-cause analysis"], accent: "purple" },
  { num: "03", icon: GitBranch, title: "Strategy", body: "The five-pillar framework — Perception, Privacy, Politics, Policing, Policy — is deployed, each pillar assigned its role. Strategy is not a document. It is an operational plan driving coordinated action.", tools: ["Penta P's Framework"], accent: "lime" },
  { num: "04", icon: Workflow, title: "Orchestration", body: "Cross-functional teams operate in unison through a single command interface, reducing decision time from 24-72h down to under 1h, with 95% coordination success across every engagement.", tools: ["Real-time coordination"], accent: "cyan" },
  { num: "05", icon: Radar, title: "Amplification", body: "Content deploys at scale through the integrated content engine, reaching audiences through 1,000+ distribution points with platform-specific optimization. 95-98% organic authenticity.", tools: ["Content distribution", "Audience optimization"], accent: "coral" },
  { num: "06", icon: Send, title: "Feedback", body: "Continuous monitoring, sentiment tracking, and outcome measurement flow back into the intelligence stage. Early warning systems detect negative trends before they become crises. The loop never closes — it accelerates.", tools: ["Continuous monitoring", "Sentiment tracking"], accent: "emerald" },
];

const TEXT: Record<string, string> = {
  red: "text-red", purple: "text-purple", lime: "text-lime", cyan: "text-cyan", coral: "text-coral", emerald: "text-emerald",
};

export function MethodologySection() {
  return (
    <Section id="methodology" ariaLabel="The Methodology" background="default" pattern>
      <SectionHeader
        icon={Compass}
        overline="The Methodology"
        overlineClassName="text-purple"
        pillClassName="glass-tinted-purple border-purple/20"
        title={
          <>
            Six-Stage <span className="text-gradient-royal">Sovereign Operations Architecture</span>
          </>
        }
        subtitle="Every engagement follows a battle-tested six-stage operating system, refined over hundreds of engagements across eighteen countries."
      />

      <div className="relative">
        {/* Connecting line for desktop */}
        <div aria-hidden className="hidden lg:block absolute top-24 left-0 right-0 h-px bg-gradient-to-r from-transparent via-red/30 to-transparent" />

        <div className="grid lg:grid-cols-3 gap-4 lg:gap-5">
          {STAGES.map((s, i) => (
            <motion.div
              key={s.num}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="group relative glass-card rounded-2xl p-6 overflow-hidden border border-border/30 hover:scale-[1.01] transition-all"
            >
              <div aria-hidden className="absolute -top-16 -right-16 size-48 rounded-full bg-current opacity-[0.04] blur-2xl group-hover:scale-150 transition-transform duration-700" />
              <div className="relative">
                <div className="flex items-start justify-between mb-5">
                  <div className={`size-14 rounded-2xl bg-gradient-sovereign flex items-center justify-center shadow-2xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-500`}>
                    <s.icon className="size-7 text-white" />
                  </div>
                  <div className={`text-display-sm leading-none ${TEXT[s.accent]} opacity-40 font-bold`}>{s.num}</div>
                </div>
                <h3 className="text-heading-md mb-3">{s.title}</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">{s.body}</p>
                <div className="flex flex-wrap gap-1.5">
                  {s.tools.map((p) => (
                    <span key={p} className="text-caption px-2.5 py-1 rounded-md bg-muted/40 text-muted-foreground border border-border/30">
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <div className="mt-10 glass-strong rounded-2xl p-6 md:p-8 gradient-border text-center">
        <h3 className="text-heading-md mb-2">One closed loop. Six stages. Verified outcomes.</h3>
        <p className="text-body-md text-muted-foreground mb-5 max-w-2xl mx-auto">
          This is the operational architecture. The loop never closes — it accelerates. Every cycle smarter than the last.
        </p>
        <Link href="/expertise/methodology/" className="inline-flex items-center gap-2 text-body-md font-semibold text-purple hover:gap-3 transition-all group">
          Explore the methodology in detail <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </Section>
  );
}
